import * as React from 'react';

import FameProgress from 'components/molecules/Fame/Progress';
import { FAME_ROW_UNITS_UNITS } from 'components/molecules/Fame/Row/FameRow';
import { EventLevelsValues } from 'defs/events';
import i18n from 'helper/i18n';
import { MINIMAL_BMI } from 'helper/validator/bmi';
import { DateTimeType } from 'types/DateTime';

export type EnumECType =
	| 'EC-1'
	| 'EC-2'
	| 'EC-3'
	| 'EC-4'
	| 'EC-5'
	| 'EC-5'
	| 'EC-6'
	| 'EC-7'
	| 'EC-8'
	| 'EC-9'
	| 'EC-10'
	| 'EC-11'
	| 'EC-12'
	| 'default';

export type GetPositionType = {
	overlap: boolean;
	overlapRecord: boolean;
	actual: {
		percent: number;
		floatText: string;
		pushText: string;
		large: boolean;
	};
	record: {
		percent: number;
		floatText: string;
		pushText: string;
	};
};

export type BadgeType = {
	id: string;
	level: string;
	eventType: string;
	attributes: {
		threshold: number;
	};
	code: string;
	eventLevel: EventLevelsValues;
	modifier?: string;
	awardedDateTime?: DateTimeType | null;
};

export type ProgressData = {
	i18n: string;
	current: number;
	next?: number;
	badges?: Array<BadgeType>;
	maxThreshold: number;
};

export type ProgressLongestRunRecordData = {
	current: number;
	record: number;
	badges?: Array<BadgeType>;
	maxThreshold: number;
};

export type ProgressWithRecordData = {
	current: number;
	record: number;
	badges?: Array<BadgeType>;
	currentDiff: number;
	maxThreshold: number;
};

export type FameProgressWrapperProps = {
	longestRun: ProgressLongestRunRecordData;
	weight: ProgressWithRecordData;
	recipes: ProgressData;
	tips: ProgressData;
	todos: ProgressData;
	tasks: ProgressData;
	workouts: ProgressData;
	total: Object;
	thresholds: {
		nextLevel?: number;
		underweight: number;
	};
	bmi: number;
};

export const calculatePercentageRange = (current: number = 0, next: number = 0): number => {
	const range = (100 / next) * current;
	return range > 100 ? 100 : range;
};

/**
 * Same as calculatePercentageRange but returns 90 if current progress is over max
 */
export const getActualPercentRange = (current: number, record: number, maxThreshold: number): number => {
	const percent =
		current >= maxThreshold && current !== record ? 90 : calculatePercentageRange(current, maxThreshold);
	return percent > 0 ? percent : 0;
};

/**
 * Returns true if close to each other using percentage
 */
export const getIsCloseTo = (value1: number, value2: number, maxThreshold: number, percentage: number) => {
	return (Math.abs(value1 - value2) / maxThreshold) * 100 < percentage;
};

const SCALE_SIDES = {
	LEFT: 'left',
	RIGHT: 'right',
	CENTER: 'center',
};

export const getThresholdsFromBadges = (badges: Array<BadgeType>): Array<number> => {
	if (badges && badges.length !== 0) {
		return badges.map((badge) => badge.attributes.threshold);
	}
	return [];
};

// Return true if value matches one of the steps
export const getStrokeHigh = (value: number, badges: any[]): boolean => {
	const steps = getThresholdsFromBadges(badges);
	return !!(value && steps.includes(value));
};

export const getIsRecordActive = (current: number, record: number, maxThreshold: number) => {
	if (!record) {
		return false;
	}
	return record !== 0 && record !== current && !(record && record >= maxThreshold);
};

export const getRecordTitle = (current: number, record: number): string => {
	if (!record) {
		return '';
	}
	return record !== 0 && record !== current ? i18n('react.impulsCoach.components.fame.progress.label.record') : '';
};

export const getRecordLabel = (current: number, record: number, unit: 'kg' | 'days'): string => {
	if (!record) {
		return '';
	}
	return record !== 0 && record !== current
		? `${record || 0} ${i18n(`react.impulsCoach.components.fame.progress.label.${unit}`, null, record)}`
		: '';
};

export const getActualTitle = (current: number, record: number): string => {
	if (!record) {
		return '';
	}
	return current !== record
		? i18n('react.impulsCoach.components.fame.progress.label.current')
		: current === record
		? i18n('react.impulsCoach.components.fame.progress.label.currentAndRecord')
		: '';
};
export const getHasActualOverlap = (current: number, maxThreshold: number): boolean => {
	if (!current) {
		return false;
	}
	return current > maxThreshold;
};

export const getHasRecordOverlap = (record: number, maxThreshold: number): boolean => {
	if (!record) {
		return false;
	}
	return record > maxThreshold;
};

export const getWeightRecordUntilNextBadge = (weight: ProgressWithRecordData): number => {
	if (!weight.badges || (weight.badges && weight.badges.length < 1)) {
		return 0;
	}

	const record: number = weight.record;
	let nextThreshold: number | null = null;

	weight.badges.forEach((badge) => {
		const threshold = badge && badge.attributes ? parseInt(badge.attributes.threshold as any, 10) : 0;
		if (nextThreshold === null && threshold > record) {
			nextThreshold = threshold - record;
		}
	});

	const value = nextThreshold === null ? 0 : nextThreshold;
	return Math.round(value * 10) / 10;
};

export const getWeightAdditionText = (weight: any): string => {
	const diff = getWeightRecordUntilNextBadge(weight);
	if (diff > 0) {
		return i18n('react.impulsCoach.components.fame.progress.weight.text', { next: diff }, diff);
	}
	return i18n('react.impulsCoach.components.fame.progress.weight.text.allBadgesReached');
};

export const getECNum = (current: number, record: number, maxThreshold: number): EnumECType => {
	// EC-1: Aktuell < 20% & Differenz = 0%
	if (getIsCloseTo(0, current, maxThreshold, 20) && current === record) {
		return 'EC-1';
	}

	// EC-2 (Default): Aktuell <= 80% & Differenz = 0%
	if (getIsCloseTo(0, current, maxThreshold, 80) && current === record) {
		return 'EC-2';
	}

	if (current !== record && record >= maxThreshold && getIsCloseTo(maxThreshold, current, maxThreshold, 20)) {
		return 'EC-3';
	}

	// EC-4: Aktuell >= 100% & Differenz > 0%
	if (current >= maxThreshold && record > current) {
		return 'EC-4';
	}

	// EC-5: Aktuell < 20% & Differenz < 18%
	if (getIsCloseTo(0, current, maxThreshold, 20) && getIsCloseTo(current, record, maxThreshold, 18)) {
		return 'EC-5';
	}

	// EC-6: Aktuell < 20% & Differenz < 25%
	if (getIsCloseTo(0, current, maxThreshold, 20) && getIsCloseTo(current, record, maxThreshold, 25)) {
		return 'EC-6';
	}

	// TODO not reachable
	// EC-7: Aktuell < 10% & Differenz <= 20%
	if (getIsCloseTo(0, current, maxThreshold, 10) && getIsCloseTo(current, record, maxThreshold, 21)) {
		return 'EC-7';
	}

	// TODO not reachable
	// EC-8: Aktuell < 20% & Differenz < 10%
	if (getIsCloseTo(0, current, maxThreshold, 20) && getIsCloseTo(current, record, maxThreshold, 10)) {
		return 'EC-8';
	}

	// EC-9: Aktuell > 90% & Differenz > 0%
	if (getIsCloseTo(maxThreshold, current, maxThreshold, 90) && current === record) {
		return 'EC-9';
	}

	// EC-10: Differenz < 10%
	if (getIsCloseTo(current, record, maxThreshold, 10)) {
		return 'EC-10';
	}

	// EC-11: Aktuell < 20%
	if (getIsCloseTo(0, current, maxThreshold, 20)) {
		return 'EC-11';
	}

	// EC-12: Aktuell < 20%
	if (getIsCloseTo(current, record, maxThreshold, 20)) {
		return 'EC-12';
	}

	return 'default';
};

const getProgressBadgesIntroText = (key: string, compilationData: any): string => {
	const { current, maxThreshold } = compilationData;
	const doesOverlap = current >= maxThreshold;
	const baseLabel = 'react.impulsCoach.components.fame.progress.detail';

	return i18n(`${baseLabel}.${key}.text${doesOverlap ? '.mastered' : ''}`, compilationData);
};

export default class FameProgressWrapper extends React.Component<FameProgressWrapperProps> {
	public static displayName = 'Wrapper(Fame(Progress))';

	get detailProgressValues(): Array<ProgressData> {
		return [this.props.recipes, this.props.tips, this.props.todos, this.props.workouts];
	}

	get longestRun(): ProgressLongestRunRecordData {
		return this.props.longestRun;
	}

	get weight(): ProgressWithRecordData {
		return this.props.weight;
	}

	public fullfillBadges(badges?: Array<BadgeType>): Array<Object> {
		if (!badges || badges.length === 0) {
			return new Array(3).fill({});
		}
		return badges;
	}

	public updateBadges(badges: Array<BadgeType>, data: Object): Array<BadgeType> {
		return badges.map((badge) => {
			return { ...badge, ...data };
		});
	}

	public getPosition(current: number, record: number, maxThreshold: number): GetPositionType {
		const data: GetPositionType = {
			overlap: false,
			overlapRecord: false,
			actual: {
				percent: getActualPercentRange(current, record, maxThreshold),
				floatText: '',
				pushText: '',
				large: !!(current === record),
			},
			record: {
				percent: calculatePercentageRange(record, maxThreshold),
				floatText: '',
				pushText: '',
			},
		};

		const map: { [key: string]: () => any } = {
			'EC-1'() {
				data.actual = {
					...data.actual,
					floatText: SCALE_SIDES.RIGHT,
					title: i18n('react.impulsCoach.components.fame.progress.label.currentAndRecord'),
				} as any;
				return data;
			},
			'EC-2'() {
				data.actual = {
					...data.actual,
					title: i18n('react.impulsCoach.components.fame.progress.label.currentAndRecord'),
				} as any;
				return data;
			},
			'EC-3'() {
				data.overlapRecord = true;
				data.actual = { ...data.actual, percent: 90, pushText: SCALE_SIDES.LEFT };
				return data;
			},
			'EC-4'() {
				data.overlap = true;
				data.actual = { ...data.actual, percent: 90, pushText: SCALE_SIDES.LEFT };
				return data;
			},
			'EC-5'() {
				data.actual = { ...data.actual, floatText: SCALE_SIDES.RIGHT };
				data.record = {
					...data.record,
					floatText: SCALE_SIDES.RIGHT,
					pushText: SCALE_SIDES.RIGHT,
				};
				return data;
			},
			'EC-6'() {
				data.actual = { ...data.actual, floatText: SCALE_SIDES.RIGHT };
				data.record = { ...data.record, floatText: SCALE_SIDES.RIGHT };
				return data;
			},
			'EC-7'() {
				data.actual = { ...data.actual, floatText: SCALE_SIDES.RIGHT };
				data.record = { ...data.record, floatText: SCALE_SIDES.RIGHT };
				return data;
			},
			'EC-8'() {
				data.record = { ...data.record, pushText: SCALE_SIDES.RIGHT };
				return data;
			},
			'EC-9'() {
				data.actual = {
					...data.actual,
					floatText: SCALE_SIDES.LEFT,
					title: i18n('react.impulsCoach.components.fame.progress.label.currentAndRecord'),
				} as any;
				return data;
			},
			'EC-10'() {
				data.actual = { ...data.actual, floatText: SCALE_SIDES.LEFT };
				data.record = { ...data.record, floatText: SCALE_SIDES.RIGHT };
				return data;
			},
			'EC-11'() {
				data.actual = { ...data.actual, floatText: SCALE_SIDES.RIGHT };
				return data;
			},
			'EC-12'() {
				data.actual = { ...data.actual, floatText: SCALE_SIDES.LEFT };
				data.record = { ...data.record, floatText: SCALE_SIDES.RIGHT };
				return data;
			},
			default() {
				return data;
			},
		};

		return map[getECNum(current, record, maxThreshold)]();
	}

	public render() {
		const { recipes, tips, todos, weight, longestRun, workouts, tasks = {}, bmi } = this.props;
		const weightPosition = this.getPosition(weight.currentDiff, weight.record, weight.maxThreshold);
		const longestRunPosition = this.getPosition(longestRun.current, longestRun.record, longestRun.maxThreshold);

		return (
			<React.Fragment>
				<FameProgress
					title={i18n('react.impulsCoach.components.fame.progress.total.title')}
					text={i18n('react.impulsCoach.components.fame.progress.total.text')}
					progress={[
						{
							modifier: 'todo',
							current: todos.current,
							label: i18n(
								'react.impulsCoach.components.fame.progress.total.progressText',
								null,
								todos.current
							),
							sticker: 'S05-0.svg',
							progress: calculatePercentageRange(todos.current, todos.maxThreshold),
							overlap: todos.current > todos.maxThreshold,
							steps: [],
							badges: {
								list: this.fullfillBadges(todos.badges),
							},
						},
					]}
				/>
				<FameProgress
					title={i18n('react.impulsCoach.components.fame.progress.detail.title')}
					text={i18n('react.impulsCoach.components.fame.progress.detail.text')}
					progress={[
						{
							modifier: 'recipe',
							current: recipes.current,
							label: i18n(
								'react.impulsCoach.components.fame.progress.detail.recipes.progressText',
								null,
								recipes.current
							),
							sticker: 'S02-0.svg',
							progress: calculatePercentageRange(recipes.current, recipes.maxThreshold),
							overlap:
								recipes.current >
								recipes.maxThreshold /* TODO: Implement other mechanism to prevent multiprop */,
							badges: {
								intro: getProgressBadgesIntroText('recipes', recipes),
								// intro: i18n(`react.impulsCoach.components.fame.progress.detail.recipes.text`, recipes),
								list: this.fullfillBadges(recipes.badges),
							},
						},
						{
							modifier: 'workout',
							current: workouts.current,
							label: i18n(
								'react.impulsCoach.components.fame.progress.detail.workouts.progressText',
								null,
								workouts.current
							),
							sticker: 'S01-0.svg',
							progress: calculatePercentageRange(workouts.current, workouts.maxThreshold),
							overlap: workouts.current > workouts.maxThreshold,
							badges: {
								intro: getProgressBadgesIntroText('workouts', workouts),
								/* intro: i18n(
									`react.impulsCoach.components.fame.progress.detail.workouts.text`,
									workouts
								),*/
								list: this.fullfillBadges(workouts.badges),
							},
						},
						{
							modifier: 'tip',
							current: tips.current,
							label: i18n(
								'react.impulsCoach.components.fame.progress.detail.tips.progressText',
								null,
								tips.current
							),
							sticker: 'S03-0.svg',
							progress: calculatePercentageRange(tips.current, tips.maxThreshold),
							overlap: tips.current > tips.maxThreshold,
							badges: {
								intro: getProgressBadgesIntroText('tips', tips),
								// intro: i18n(`react.impulsCoach.components.fame.progress.detail.tips.text`, tips),
								list: this.fullfillBadges(tips.badges),
							},
						},
						{
							modifier: 'task',
							current: (tasks as any).current,
							sticker: 'S04-0.svg',
							label: i18n(
								'react.impulsCoach.components.fame.progress.detail.tasks.progressText',
								null,
								(tasks as any).current
							),
							progress: calculatePercentageRange((tasks as any).current, (tasks as any).maxThreshold),
							overlap: (tasks as any).current > (tasks as any).maxThreshold,
							badges: {
								intro: getProgressBadgesIntroText('tasks', tasks as any),
								// intro: i18n(`react.impulsCoach.components.fame.progress.detail.tasks.text`, tasks),
								list: this.fullfillBadges((tasks as any).badges),
							},
						},
					]}
				/>
				<FameProgress
					title={i18n('react.impulsCoach.components.fame.progress.series.title')}
					text={i18n(
						`react.impulsCoach.components.fame.progress.series.text${
							longestRun.record >= longestRun.maxThreshold ? '.mastered' : ''
						}`
					)}
					addition={{
						value: longestRun.record,
						text: i18n(
							'react.impulsCoach.components.fame.progress.series.progressText',
							null,
							longestRun.record
						),
					}}
					progress={[
						{
							modifier: 'overall',
							current: longestRun.current > longestRun.record ? longestRun.current : longestRun.record,
							label: i18n(
								'react.impulsCoach.components.fame.progress.series.progressText',
								null,
								longestRun.current
							),
							progress: calculatePercentageRange(longestRun.current, longestRun.maxThreshold),
							record: {
								percent: longestRunPosition.record.percent,
								progress: longestRun.record,
								active: getIsRecordActive(
									longestRun.current,
									longestRun.record,
									longestRun.maxThreshold
								),
								title: getRecordTitle(longestRun.current, longestRun.record),
								label: getRecordLabel(longestRun.current, longestRun.record, 'days'),
								floatText: longestRunPosition.record.floatText,
								pushText: longestRunPosition.record.pushText,
								strokeHigh: getStrokeHigh(longestRun.record || 0, longestRun.badges || []),
							},
							actual: {
								percent: longestRunPosition.actual.percent,
								active: !(longestRun.current >= longestRun.maxThreshold),
								title: getActualTitle(longestRun.current, longestRun.record),
								label: `${longestRun.current} ${i18n(
									'react.impulsCoach.components.fame.progress.label.days',
									null,
									longestRun.current
								)}`,
								floatText: longestRunPosition.actual.floatText,
								pushText: longestRunPosition.actual.pushText,
								strokeHigh: getStrokeHigh(longestRun.current || 0, longestRun.badges || []),
								large: longestRunPosition.actual.large,
							},
							unit: FAME_ROW_UNITS_UNITS.days as any,
							overlap: longestRunPosition.overlap,
							overlapRecord: longestRunPosition.overlapRecord as any,
							badges: {
								intro: '',
								list: this.fullfillBadges(longestRun.badges),
							},
						} as any,
					]}
				/>
				{bmi && bmi >= MINIMAL_BMI && (
					<FameProgress
						title={i18n('react.impulsCoach.components.fame.progress.weight.title')}
						text={getWeightAdditionText(weight)}
						addition={{
							value: weight.record,
							text: i18n(
								'react.impulsCoach.components.fame.progress.weight.progressText',
								null,
								weight.record
							),
						}}
						progress={[
							{
								modifier: 'overall',
								current: weight.current > weight.record ? weight.current : weight.record,
								label: i18n(
									'react.impulsCoach.components.fame.progress.weight.progressText',
									null,
									weight.currentDiff
								),
								progress: calculatePercentageRange(weight.currentDiff, weight.maxThreshold),
								record: {
									percent: weightPosition.record.percent,
									progress: weight.record,
									active: getIsRecordActive(weight.currentDiff, weight.record, weight.maxThreshold),
									title: getRecordTitle(weight.currentDiff, weight.record),
									label: getRecordLabel(weight.currentDiff, weight.record, 'kg'),
									floatText: weightPosition.record.floatText,
									pushText: weightPosition.record.pushText,
									strokeHigh: getStrokeHigh(weight.record || 0, weight.badges || []),
								},
								actual: {
									percent: weightPosition.actual.percent,
									active: !(weight.currentDiff >= weight.maxThreshold),
									title: getActualTitle(weight.currentDiff, weight.record),
									label: `${weight.currentDiff > 0 ? weight.currentDiff : 0} ${i18n(
										'react.impulsCoach.components.fame.progress.label.kg.singular'
									)}`,
									floatText: weightPosition.actual.floatText,
									pushText: weightPosition.actual.pushText,
									strokeHigh: getStrokeHigh(weight.currentDiff || 0, weight.badges || []),
									large: weightPosition.actual.large,
								},
								unit: FAME_ROW_UNITS_UNITS.kg as any,
								overlap: weightPosition.overlap,
								overlapRecord: weightPosition.overlapRecord,
								badges: {
									intro: '',
									list: this.fullfillBadges(weight.badges),
								},
							} as any,
						]}
					/>
				)}
			</React.Fragment>
		);
	}
}
